import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment';
import PropTypes from 'prop-types';
import keyService from '../../services/keyService';
import './ListMessages.css';

class MessageRow extends Component {
  constructor(props) {
    super(props);
    this.state = {
      showDetails: false,
      deleting: false,
      error: null
    };
    this.handleDeleteClick = this.handleDeleteClick.bind(this);
    this.toggleDetails = this.toggleDetails.bind(this);
  }

  async handleDeleteClick(e) {
    e.preventDefault();
    
    if (window.confirm('Are you sure you want to delete this message?')) {
      this.setState({ deleting: true });
      try {
        await keyService.deleteMessage(this.props.id);
        this.props.onMessageDeleted(this.props.id);
      } catch (error) {
        this.setState({ 
          error: 'Failed to delete message', 
          deleting: false 
        });
      }
    }
  }

  toggleDetails() {
    this.setState({ showDetails: !this.state.showDetails });
  }

  render() {
    const { id, subject, created_at, keys } = this.props;
    const formattedDate = moment(created_at).format('MMM D, YYYY');
    
    return (
      <div className="message-item">
        <div className="message-item-header">
          <div className="message-main-info">
            <h4 className="message-subject">
              <i className="fa fa-file-lines message-icon" aria-hidden="true"></i> 
              <span className="message-label">Subject:</span> 
              <span className="message-value">{subject || 'Untitled Document'}</span>
            </h4>
            <p className="message-date">
              <i className="fa fa-calendar message-icon" aria-hidden="true"></i> 
              <span className="message-label">Created:</span> 
              <span className="message-value">{formattedDate}</span>
            </p>
          </div>
          <div className="message-actions">
            <Link 
              to={`/encrypted_text_view/${id}`}
              className="btn btn-primary btn-sm action-button view-button"
              aria-label={`View document: ${subject || 'Untitled Document'}`}
            >
              <i className="fa fa-eye" aria-hidden="true"></i> View
            </Link>
            <button 
              className="btn btn-outline-danger btn-sm action-button delete-button"
              onClick={this.handleDeleteClick}
              disabled={this.state.deleting}
              aria-label={`Delete document: ${subject || 'Untitled Document'}`}
            >
              {this.state.deleting ? (
                <><i className="fa fa-spinner fa-spin" aria-hidden="true"></i> Deleting...</>
              ) : (
                <><i className="fa fa-trash" aria-hidden="true"></i> Delete</>
              )}
            </button>
          </div>
        </div>
        
        <div className="message-item-details">
          <button 
            className="message-details-toggle"
            onClick={this.toggleDetails}
            aria-expanded={this.state.showDetails}
            aria-controls={`details-${id}`}
          >
            <i className={`fa fa-chevron-${this.state.showDetails ? 'up' : 'down'}`} aria-hidden="true"></i> 
            {this.state.showDetails ? 'Hide' : 'Show'} details
          </button>
          
          {this.state.showDetails && (
            <div className="message-details-container" id={`details-${id}`}>
              <div className="message-detail-row">
                <span className="detail-label">Document ID:</span> 
                <span className="detail-value">{id}</span>
              </div>
              
              {keys && keys.length > 0 && (
                <div className="message-keys-section">
                  <h5 className="keys-heading">
                    <i className="fa fa-key" aria-hidden="true"></i> Associated Keys
                  </h5>
                  <ul className="keys-list">
                    {keys.map(keyData => (
                      <li key={keyData.key_id} className="key-item">
                        <Link to={`/keys`} className="key-link">
                          <i className="fa fa-key key-icon" aria-hidden="true"></i>
                          <span className="key-name">{keyData.keys?.name || 'Unnamed Key'}</span>
                          <i className="fa fa-arrow-up-right-from-square external-link-icon" aria-hidden="true"></i>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}
          
          {this.state.error && (
            <div className="alert alert-danger mt-2" role="alert">
              <i className="fa fa-exclamation-circle" aria-hidden="true"></i> {this.state.error}
            </div>
          )}
        </div>
      </div>
    );
  }
}

MessageRow.propTypes = {
  id: PropTypes.string.isRequired,
  subject: PropTypes.string,
  created_at: PropTypes.string.isRequired,
  keys: PropTypes.array,
  onMessageDeleted: PropTypes.func.isRequired
};

class ListMessages extends Component {
  constructor() {
    super();
    this.state = {
      messages: null,
      isLoading: true,
      error: null
    };
    this.fetchMessages = this.fetchMessages.bind(this);
    this.handleMessageDeleted = this.handleMessageDeleted.bind(this);
  }

  componentDidMount() {
    this.fetchMessages();
  }

  async fetchMessages() {
    try {
      const messages = await keyService.getMessages();
      this.setState({ messages, isLoading: false });
    } catch (error) {
      this.setState({ 
        error: error.message || 'Failed to load messages', 
        isLoading: false 
      });
    }
  }

  handleMessageDeleted(deletedMessageId) {
    this.setState({
      messages: this.state.messages.filter(message => message.id !== deletedMessageId)
    });
  }

  render() {
    const { messages, isLoading, error } = this.state;
    
    if (isLoading) {
      return (
        <div className="loading-container text-center p-4">
          <i className="fa fa-spinner fa-spin fa-2x" aria-hidden="true"></i>
          <p className="loading-text mt-2">Loading document history...</p>
        </div>
      );
    }
    
    if (error) {
      return (
        <div className="alert alert-danger error-container" role="alert">
          <i className="fa fa-exclamation-triangle" aria-hidden="true"></i> 
          <span className="error-message">Error loading document history: {error}</span>
        </div>
      );
    }
    
    if (!messages || messages.length === 0) {
      return (
        <div className="empty-state-container">
          <div className="text-center p-4">
            <i className="fa fa-file-lines fa-3x text-muted mb-3" aria-hidden="true"></i>
            <h4 className="empty-state-heading">No Secured Documents Found</h4>
            <p className="empty-state-message text-muted">Create a new secured document to get started.</p>
            <Link to="/create_encrypted_text" className="btn btn-primary create-button mt-3">
              <i className="fa fa-plus" aria-hidden="true"></i> Create New Document
            </Link>
          </div>
        </div>
      );
    }
    
    return (
      <div className="messages-list">
        <div className="messages-header">
          <h3 className="messages-list-title">
            <i className="fa fa-history" aria-hidden="true"></i> Document History
            <span className="document-count">{messages.length}</span>
          </h3>
          <Link to="/create_encrypted_text" className="btn btn-primary add-document-button">
            <i className="fa fa-plus" aria-hidden="true"></i> New Document
          </Link>
        </div>
        
        <div className="messages-list-container">
          {messages.map(message => (
            <MessageRow
              key={message.id}
              id={message.id}
              subject={message.subject}
              created_at={message.created_at}
              keys={message.keys}
              onMessageDeleted={this.handleMessageDeleted}
            />
          ))}
        </div>
      </div>
    );
  }
}

export default ListMessages;
